import { Box, Typography, Paper, Button, TextField, InputAdornment, Snackbar, Alert} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Tooltip from "@mui/material/Tooltip";
import EmailIcon from "@mui/icons-material/Email";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { useApiClient } from "../useApiClient";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { API_CONFIG, getApiUrl, NAVIGATION_CONFIG } from "../config/api";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Phone } from "../types/phone";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { borderRadius } from '../theme';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

interface Customer {
  externalId: string;
  name: string;
  birthDate: string;
  shortAddress: string;
  phones: Phone[];
  customerType: string;
}

export default function Customers() {
  // Search state for each field
  const [searchFields, setSearchFields] = useState({
    name: "",
    birthDate: "",
    phone: "",
    postcode: "",
    email: "",
  });

  // Helper to reset all except one field
  const handleSearchFieldChange = (field: string, value: string) => {
    setSearchFields((prev) => {
      const resetFields = { name: "", birthDate: "", phone: "", postcode: "", email: ""};
      return { ...resetFields, [field]: value };
    });
  };

  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error" | "warning">("success");

  // Toast helper method
  const showToast = (message: string, severity: "success" | "error" | "warning" = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  // Clear filter handler
  const handleClearFilters = () => {
    setSearchFields({ name: "", birthDate: "", phone: "", postcode: "", email: "" });
    setDobInputTempValue(null);
    setActiveFilter(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Handle search button click
  const handleSearch = () => {
    const field = Object.keys(searchFields).find(
      (key) => searchFields[key as keyof typeof searchFields]
    );
    if (!field) {
      setActiveFilter(null);
      return;
    }

    // Check if the search value has at least 3 characters
    const searchValue = searchFields[field as keyof typeof searchFields];
    if (searchValue.length < 3) {
      showToast("Please enter at least 3 characters to search", "warning");
      return;
    }

    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page
    setActiveFilter({
      field,
      value: searchFields[field as keyof typeof searchFields],
    });
  };

  // Track active filter for server
  const [activeFilter, setActiveFilter] = useState<{
    field: string;
    value: string;
  } | null>(null);
  const { callApi } = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    () => {
      if (location.state?.returnedFromDetails) {
        return {
          page: location.state.page,
          pageSize: location.state.pageSize,
        };
      }
      return { page: 0, pageSize: 10 };
    }
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const fetchCustomerData = async (
    page: number,
    pageSize: number,
    sortModel: GridSortModel
  ) => {
    try {
      setLoading(true);
      // Build sort query string
      let sortQuery = "";
      if (sortModel && sortModel.length > 0) {
        const sort = sortModel[0];
        sortQuery = `&sortBy=${sort.field}&sortDirection=${sort.sort}`;
      }
      let filterQuery = "";
      let filterField = "";
      if (activeFilter && activeFilter.value) {
        // Map field to server param
        if (activeFilter.field === "name") filterField = "name";
        if (activeFilter.field === "birthDate") filterField = "dateofbirth";
        if (activeFilter.field === "phone") filterField = "phones";
        if (activeFilter.field === "postcode") filterField = "postcode";
        if (activeFilter.field === "email") filterField = "email";

        filterQuery = `&filterBy=${filterField}&filterValue=${encodeURIComponent(
          activeFilter.value
        )}`;
      }
      const result = await callApi(
        `${getApiUrl(API_CONFIG.ENDPOINTS.CUSTOMERS)}?page=${
          page + 1
        }&pageSize=${pageSize}${sortQuery}${filterQuery}`
      );
      setCustomers(result.data.items);
      setTotalRows(result.data.totalCount);
      setError(null);
    } catch (err: any) {
      console.error("API call failed:", err);
      if (err.response?.status === 403) {
        setError("Access denied: You do not have permission.");
      } else {
        setError("An error occurred while fetching customer data.");
      }
      setCustomers([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData(
      paginationModel.page,
      paginationModel.pageSize,
      sortModel
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, sortModel]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleAddNew = () => {
    navigate(NAVIGATION_CONFIG.ROUTES.CUSTOMERS_NEW);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "dateOfBirth",
      headerName: "Birth Date",
      flex: 1,
      minWidth: 120,
      valueGetter: (params: any) => {
        if (!params) return "";
        const date = new Date(params);
        // Format as DD/MM/YYYY
        return date.toLocaleDateString("en-GB");
      },
    },
    {
      field: "phones",
      headerName: "Phone Number",
      flex: 1,
      minWidth: 130,
      valueGetter: (phones: Phone[]) => {
        if (!phones.length) return "";
        const primary = phones.find((p: any) => p.isPrimary) || phones[0];
        if (primary && typeof primary.number === "string") {
          try {
            const phoneObj = parsePhoneNumberFromString(primary.number, "GB");
            return phoneObj ? phoneObj.formatInternational() : primary.number;
          } catch {
            return primary.number;
          }
        }
        return "";
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "customerType",
      headerName: "Customer Type",
      flex: 1.5,
      minWidth: 30,
      maxWidth: 200,
      sortable: false
    },
    {
      field: "shortAddress",
      headerName: "Address",
      flex: 1.5,
      minWidth: 150,
    },
  ];

  const [dobInputTempValue, setDobInputTempValue] = useState<dayjs.Dayjs | null>(
    searchFields.birthDate ? dayjs(searchFields.birthDate) : null
  );

  return (
    <Box sx={{ pl:3, pr:0}}>
      {/* Top card for title and add button inside Paper */}
      <Paper
        sx={{
          mb: 3,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flexWrap: "wrap",
        }}
        elevation={5}
      >
        {/* First line: title and add button */}
        <Box
          sx={{ display: "flex", alignItems: "center", width: "100%", mb: 1 }}
        >
          <Typography variant="h4" sx={{ flex: 1 }}>
            Customers
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ borderRadius: borderRadius.mui.medium }}
          >
            Add Customer
          </Button>
        </Box>
        {/* Second line: filters */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            variant="outlined"
            placeholder="Name"
            value={searchFields.name}
            onChange={(e) => handleSearchFieldChange("name", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Birth Date"
              format="DD/MM/YYYY"
              value={dobInputTempValue}
              onChange={(newValue) => {
                setDobInputTempValue(dayjs(newValue));
                if (newValue && dayjs(newValue).isValid()) {
                  const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
                  handleSearchFieldChange("birthDate", formattedDate);
                } else {
                  handleSearchFieldChange("birthDate", "");
                }
              }}
              // onAccept={(newValue) => {
              //   if (newValue instanceof Date && isValid(newValue)) {
              //     setDobPickerValue(dayjs(newValue));
              //     const formattedDate = newValue ? dayjs(newValue).format('YYYY-MM-DD') : '';
              //     handleSearchFieldChange("birthDate", formattedDate);
              //   }
              // }}
              // onClose={() => {
              //   if (dobInputTempValue instanceof Date && isValid(dobInputTempValue)) {
              //     setDobPickerValue(dobInputTempValue);
              //     const formattedDate = dobInputTempValue ? dobInputTempValue.format('YYYY-MM-DD') : '';
              //     handleSearchFieldChange("birthDate", formattedDate);
              //   }
              // }}
              slotProps={{
                textField: {
                  sx: { flex: 1, minWidth: 160 },
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarTodayIcon />
                      </InputAdornment>
                    ),
                  },
                },
              }}
            />
          </LocalizationProvider>

          <TextField
            variant="outlined"
            placeholder="Phone"
            value={searchFields.phone}
            onChange={(e) => handleSearchFieldChange("phone", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <TextField
            variant="outlined"
            placeholder="Email"
            value={searchFields.email}
            onChange={(e) => handleSearchFieldChange("email", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <TextField
            variant="outlined"
            placeholder="Postcode"
            value={searchFields.postcode}
            onChange={(e) =>
              handleSearchFieldChange("postcode", e.target.value)
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LocationOnIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <Tooltip title="Search">
            <IconButton
              aria-label="Search"
              onClick={handleSearch}
              color="primary"
              sx={{ ml: 1 }}
              size="large"
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reset filters">
            <IconButton
              aria-label="Clear filters"
              onClick={handleClearFilters}
              sx={{ ml: 1 }}
              size="large"
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      {/* Main grid with shadow */}
      {error ? (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        // <Box
        //   sx={{
        //     width: "100%",
        //     minHeight: 400,
        //     boxShadow: 2,
        //     borderRadius: 2,
        //   }}
        // >
        <Paper elevation={5}>
          <DataGrid
            rows={customers}
            columns={columns}
            getRowId={(row) => row.externalId}
            paginationMode="server"
            sortingMode="server"
            pageSizeOptions={[5, 10, 25]}
            paginationModel={paginationModel}
            rowCount={totalRows}
            loading={loading}
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            onRowClick={(params) =>
              navigate(NAVIGATION_CONFIG.getCustomerDetailRoute(params.row.externalId), {
                state: {
                  fromPage: paginationModel.page,
                  pageSize: paginationModel.pageSize,
                },
              })
            }
            sx={{
              border: 0,
              "& .MuiDataGrid-row": {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              },
              "& .MuiDataGrid-main": {
                borderRadius: 0,
                backgroundColor: "transparent",
              },
              "& .MuiDataGrid-columnHeaders": {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                backgroundColor: "red",
              },
              "& .MuiDataGrid-footerContainer": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              background: "transparent",
            }}
          />
        </Paper>
        // </Box>
      )}

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setToastOpen(false)} 
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
